import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const SignUpStep2 = ({ onFormChange, isMultiStep = false, validationErrors = {}, initialData = {} }) => {
  const navigate = useNavigate();
  const { completeProfile, user } = useAuth();
  const isInitializedRef = useRef(false);
  const prevInitialDataRef = useRef(null);

  const [formData, setFormData] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    dateofbirth: "",
    address: "",
    phone_number: "",
    altMobile: "",
    fathersName: "",
    mothersName: "",
    permanentAddress: "",
    correspondenceAddress: "",
    referred_by: "",
    email: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState([]);

    // Auto-fill with Google/local user data or initialData prop
  useEffect(() => {
    // Create a stable string representation of initialData for comparison
    const initialDataString = JSON.stringify(initialData);
    
    // If we've already initialized and initialData hasn't meaningfully changed, skip
    if (isInitializedRef.current && prevInitialDataRef.current === initialDataString) {
      return;
    }
    
    // If initialData is provided (from ProfileLayout), use it first
    // Check if initialData has meaningful values (not just empty strings)
    const hasInitialData = initialData && Object.keys(initialData).length > 0 && 
                          Object.values(initialData).some(val => val !== '' && val !== null && val !== undefined);
    
    console.log('🔵 SignUpStep2 useEffect - isMultiStep:', isMultiStep, 'hasInitialData:', hasInitialData, 'initialData:', initialData);
    
    if (isMultiStep && hasInitialData) {
      console.log('✅ Using initialData from ProfileLayout to populate form');
      let newFormData = {
        first_name: initialData.first_name || "",
        middle_name: initialData.middle_name || "",
        last_name: initialData.last_name || "",
        dateofbirth: initialData.dateofbirth || "",
        address: initialData.address || "",
        phone_number: initialData.phone_number || "",
        altMobile: initialData.altMobile || "",
        fathersName: initialData.fathersName || "",
        mothersName: initialData.mothersName || "",
        permanentAddress: initialData.permanentAddress || "",
        correspondenceAddress: initialData.correspondenceAddress || "",
        referred_by: initialData.referred_by || "",
        email: initialData.email || "",
      };
      let filledFields = [];
      
      // Track which fields were filled
      Object.keys(newFormData).forEach(key => {
        if (newFormData[key] && newFormData[key] !== '') {
          filledFields.push(key);
        }
      });
      
      setFormData(newFormData);
      setAutoFilledFields(filledFields);
      if (onFormChange) onFormChange(newFormData);
      isInitializedRef.current = true;
      prevInitialDataRef.current = initialDataString;
      return;
    }
    
    // Only run localStorage/Google data initialization once (on mount)
    if (isInitializedRef.current && !isMultiStep) {
      return;
    }
    
    // Otherwise, use localStorage/Google data (for new signups)
    const savedUser = localStorage.getItem("currentUser");
    const googleData = localStorage.getItem("googleData");
    let newFormData = { ...formData };
    let filledFields = [];

    try {
      if (googleData) {
        const gData = JSON.parse(googleData);
        if (gData.given_name) {
          newFormData.first_name = gData.given_name;
          filledFields.push("first_name");
        }
        if (gData.middle_name) {
          newFormData.middle_name = gData.middle_name;
          filledFields.push("middle_name");
        }
        if (gData.family_name) {
          newFormData.last_name = gData.family_name;
          filledFields.push("last_name");
        }
      }
      if (savedUser) {
        const u = JSON.parse(savedUser);
        // Pre-fill all fields from existing user data (for editing)
        if (u.first_name) {
          newFormData.first_name = u.first_name;
          filledFields.push("first_name");
        }
        if (u.middle_name) {
          newFormData.middle_name = u.middle_name;
          filledFields.push("middle_name");
        }
        if (u.last_name) {
          newFormData.last_name = u.last_name;
          filledFields.push("last_name");
        }
        if (u.dateofbirth) {
          newFormData.dateofbirth = u.dateofbirth;
          filledFields.push("dateofbirth");
        }
        if (u.correspondenceAddress || u.address) {
          newFormData.address = u.correspondenceAddress || u.address;
          filledFields.push("address");
        }
        if (u.email) {
          newFormData.email = u.email;
          filledFields.push("email");
        }
        if (u.phone_number) {
          newFormData.phone_number = u.phone_number;
          filledFields.push("phone_number");
        }
        if (u.alt_phone_number) {
          newFormData.altMobile = u.alt_phone_number;
        }
        if (u.fathersName) {
          newFormData.fathersName = u.fathersName;
        }
        if (u.mothersName) {
          newFormData.mothersName = u.mothersName;
        }
        if (u.permanentAddress) {
          newFormData.permanentAddress = u.permanentAddress;
        }
        if (u.correspondenceAddress) {
          newFormData.correspondenceAddress = u.correspondenceAddress;
        }
        
        const signupData = JSON.parse(localStorage.getItem("signupData") || "{}");
        if (signupData.email) {
          newFormData.email = signupData.email;
          filledFields.push("email");
        }
      }
    } catch (err) {
      console.error("Error parsing user/google data:", err);
    }

    setFormData(newFormData);
    setAutoFilledFields(filledFields);

    if (googleData) localStorage.removeItem("googleData");

    // fire initial form change for auto-filled data
    if (onFormChange) onFormChange(newFormData);
    isInitializedRef.current = true;
    prevInitialDataRef.current = initialDataString;
  }, [initialData, isMultiStep]);

  // 🔹 handleChange now calls onFormChange
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (onFormChange) onFormChange(updated); // <-- notify parent
      return updated;
    });
    setError("");
  };

  const validateForm = () => {
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.dateofbirth ||
      !formData.address ||
      !formData.phone_number
    ) {
      setError(
        "First name, last name, date of birth, address, and phone are required."
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      let email = user?.email;
      if (!email) {
        const savedUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
        email = savedUser.email;
      }
      if (!email) {
        const signupData = JSON.parse(localStorage.getItem("signupData") || "{}");
        email = signupData.email;
      }
      if (!email) {
        setError("Email not found. Please restart signup.");
        return;
      }

      const result = await completeProfile({ ...formData, email });
      if (result.success) {
        localStorage.removeItem("signupData");
        localStorage.removeItem("googleData");
        navigate("/", { replace: true });
      } else {
        setError(result.message || "Profile update failed.");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={isMultiStep ? (e) => e.preventDefault() : handleSubmit} className="space-y-3">
      {/* Info Banner */}
      {autoFilledFields.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
          Some fields were auto-filled from your account. Please review them.
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-xs text-red-600 p-2 rounded-lg">
          {error}
        </div>
      )}

      {/* First Name */}
      <div>
        <label className="block text-xs font-semibold">First Name</label>
        <input
          type="text"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
          required
          placeholder="Enter your first name"
          className={`w-full px-3 py-2 rounded-lg border text-sm ${autoFilledFields.includes("first_name")
              ? "bg-blue-50 border-blue-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
      </div>

      {/* Middle Name */}
      <div>
        <label className="block text-xs font-semibold">Middle Name</label>
        <input
          type="text"
          name="middle_name"
          value={formData.middle_name}
          onChange={handleChange}
          placeholder="Enter your middle name (optional)"
          className={`w-full px-3 py-2 rounded-lg border text-sm ${autoFilledFields.includes("middle_name")
              ? "bg-blue-50 border-blue-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
      </div>

      {/* Last Name */}
      <div>
        <label className="block text-xs font-semibold">Last Name <span className="text-red-500">*</span></label>
        <input
          type="text"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
          required
          placeholder="Enter your last name"
          className={`w-full px-3 py-2 rounded-lg border text-sm ${autoFilledFields.includes("last_name")
              ? "bg-blue-50 border-blue-300"
              : validationErrors.last_name
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
        {validationErrors.last_name && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.last_name}</p>
        )}
      </div>

      {/* DOB */}
      <div>
        <label className="block text-xs font-semibold">Date of Birth <span className="text-red-500">*</span></label>
        <input
          type="date"
          name="dateofbirth"
          value={formData.dateofbirth}
          onChange={handleChange}
          required
          className={`w-full px-3 py-2 rounded-lg border text-sm ${
              validationErrors.dateofbirth
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
        {validationErrors.dateofbirth && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.dateofbirth}</p>
        )}
      </div>

      {/* Father's Name */}
      <div>
        <label className="block text-xs font-semibold">Father's Name <span className="text-red-500">*</span></label>
        <input
          type="text"
          name="fathersName"
          value={formData.fathersName}
          onChange={handleChange}
          required
          placeholder="Enter your father's name"
          className={`w-full px-3 py-2 rounded-lg border text-sm ${
              validationErrors.fathersName
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
        {validationErrors.fathersName && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.fathersName}</p>
        )}
      </div>

      {/* Mother's Name */}
      <div>
        <label className="block text-xs font-semibold">Mother's Name <span className="text-red-500">*</span></label>
        <input
          type="text"
          name="mothersName"
          value={formData.mothersName}
          onChange={handleChange}
          required
          placeholder="Enter your mother's name"
          className={`w-full px-3 py-2 rounded-lg border text-sm ${
              validationErrors.mothersName
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
        {validationErrors.mothersName && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.mothersName}</p>
        )}
      </div>

      {/* Permanent Address */}
      <div>
        <label className="block text-xs font-semibold">Permanent Address <span className="text-red-500">*</span></label>
        <textarea
          name="permanentAddress"
          value={formData.permanentAddress}
          onChange={handleChange}
          rows="2"
          required
          placeholder="Enter your permanent address"
          className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${
              validationErrors.permanentAddress
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
        {validationErrors.permanentAddress && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.permanentAddress}</p>
        )}
      </div>

      {/* Correspondence Address */}
      <div>
        <label className="block text-xs font-semibold">Correspondence Address</label>
        <textarea
          name="correspondenceAddress"
          value={formData.correspondenceAddress}
          onChange={handleChange}
          rows="2"
          placeholder="Enter your correspondence address (if different from permanent)"
          className="w-full px-3 py-2 rounded-lg border bg-gray-50 text-sm resize-none"
        />
      </div>

      {/* Address (for backward compatibility) */}
      <div>
        <label className="block text-xs font-semibold">Current Address <span className="text-red-500">*</span></label>
        <textarea
          name="address"
          value={formData.address}
          onChange={handleChange}
          rows="2"
          required
          placeholder="Enter your current address"
          className={`w-full px-3 py-2 rounded-lg border text-sm resize-none ${
              validationErrors.address
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
        {validationErrors.address && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.address}</p>
        )}
      </div>

      {/* Email */}
     <div>
          <label className="block text-xs font-semibold">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="Enter your email"
            className={`w-full px-3 py-2 rounded-lg border text-sm ${
              autoFilledFields.includes("email")
                ? "bg-blue-50 border-blue-300"
                : "bg-gray-50 border-gray-200"
            }`}
          />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-xs font-semibold">Phone Number <span className="text-red-500">*</span></label>
        <input
          type="tel"
          name="phone_number"
          value={formData.phone_number}
          onChange={handleChange}
          required
          placeholder="Enter your phone number"
          className={`w-full px-3 py-2 rounded-lg border text-sm ${
              validationErrors.phone_number
              ? "bg-red-50 border-red-300"
              : "bg-gray-50 border-gray-200"
            }`}
        />
        {validationErrors.phone_number && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.phone_number}</p>
        )}
      </div>

      {/* Alternate Mobile */}
      <div>
        <label className="block text-xs font-semibold">Alternate Mobile (optional)</label>
        <input
          type="tel"
          name="altMobile"
          value={formData.altMobile}
          onChange={handleChange}
          placeholder="Enter alternate mobile number"
          className="w-full px-3 py-2 rounded-lg border bg-gray-50 text-sm"
        />
      </div>

      {/* Referral Code */}
      <div>
        <label className="block text-xs font-semibold">Referral Code (optional)</label>
        <input
          type="text"
          name="referred_by"
          value={formData.referred_by}
          onChange={handleChange}
          placeholder="Enter referral code"
          className="w-full px-3 py-2 rounded-lg border bg-gray-50 text-sm"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          If you have a referral code from a friend, enter it here to give them credit.
        </p>
      </div>

      {/* Submit - Only show if NOT in multi-step mode */}
      {!isMultiStep && (
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
        >
          {loading ? "Saving..." : "Save Profile"}
        </button>
      )}
    </form>
  );
};

export default SignUpStep2;
